// frontend/src/App.js
import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Layout Components
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';
import AdminLayout from './layouts/AdminLayout';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import HomePage from './pages/HomePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import VerifyPhonePage from './pages/auth/VerifyPhonePage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import WalletPage from './pages/wallet/WalletPage';
import AirtimePage from './pages/vtu/AirtimePage';
import DataPage from './pages/vtu/DataPage';
import BillsPage from './pages/bills/BillsPage';
import ServicesPage from './pages/services/ServicesPage';
import SupportPage from './pages/support/SupportPage';
import CommunityPage from './pages/community/CommunityPage';
import ProfilePage from './pages/profile/ProfilePage';
import SettingsPage from './pages/settings/SettingsPage';
import FundingCallbackPage from './pages/wallet/FundingCallbackPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import UserProfilePage from './pages/community/UserProfilePage';
import UserReportsPage from './pages/community/UserReportsPage';
import PostPage from './pages/community/PostPage';
import MarketplaceBusinessesPage from './pages/marketplace/MarketplaceBusinessesPage';
import BusinessProfilePage from './pages/community/BusinessProfilePage';

// Marketplace Pages
import ProductManagementPage from './pages/marketplace/ProductManagementPage';
import ProductListPage from './pages/marketplace/ProductListPage';
import ServiceManagementPage from './pages/marketplace/ServiceManagementPage';
import ServiceListPage from './pages/marketplace/ServiceListPage';
import OrderManagementPage from './pages/marketplace/OrderManagementPage';
import OrderSuccessPage from './pages/marketplace/OrderSuccessPage';
import MyOrdersPage from './pages/marketplace/MyOrdersPage';
import FavoritesPage from './pages/marketplace/FavoritesPage';
import MarketplacePage from './pages/marketplace/MarketplacePage';

// Admin Marketplace Pages
import AdminBusinessesPage from './pages/admin/AdminBusinessesPage';
import AdminReviewsPage from './pages/admin/AdminReviewsPage';
import AdminOrdersPage from './pages/admin/AdminOrdersPage';

// Admin Announcements & Promotions Pages
import AdminAnnouncementsPage from './pages/admin/AdminAnnouncementsPage';
import AdminPromotionsPage from './pages/admin/AdminPromotionsPage';

// Global Overlay
import GlobalOverlay from './components/common/GlobalOverlay';

// Store
import useAuthStore from './store/authStore';
import realtimeService from './services/realtime.service';

// Toast Utilities
import { showErrorToast, clearAllToasts } from './utils/toastUtils';

// Track if global handlers are set
let globalHandlersSet = false;

// Global error handler for unhandled promise rejections
const setupGlobalErrorHandlers = () => {
  if (globalHandlersSet) return;
  globalHandlersSet = true;

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;

    // ─────────────────────────────────────────────────────────
    // SKIP ERRORS ALREADY HANDLED BY THE AXIOS INTERCEPTOR
    //
    // The axios interceptor in utils/axiosConfig.js already shows
    // a toast for every failed HTTP request. If we also handle the
    // re-rejected promise here, the user sees the SAME error twice.
    //
    // Any error that came from axios will have `error.response` or
    // `error.config` set. Skip those entirely.
    // ─────────────────────────────────────────────────────────
    if (error?.response || error?.config) {
      return;
    }

    // Skip network errors — the interceptor handles those too
    if (error?.code === 'ERR_NETWORK') {
      return;
    }

    // Skip 401s — the interceptor handles session expiry
    if (error?.response?.status === 401) {
      return;
    }

    // Skip errors already toasted by the axios interceptor.
    // If the interceptor showed a toast, it marks the error so we
    // can detect and ignore it here.
    if (error?._toastShown) {
      return;
    }

    // ─────────────────────────────────────────────────────────
    // Otherwise, this is a genuine unhandled rejection that
    // didn't come from axios (e.g. a bug in some other code).
    // Show a generic friendly message.
    // ─────────────────────────────────────────────────────────
    const message = error?.message;
    if (
      message &&
      typeof message === 'string' &&
      message.length > 0 &&
      message.length < 200 &&
      !message.includes('Request failed') &&
      !message.includes('status code') &&
      !message.includes('ECONN') &&
      !message.includes('ERR_')
    ) {
      showErrorToast(message);
    } else {
      showErrorToast('An unexpected error occurred. Please try again.');
    }

    if (process.env.NODE_ENV === 'development') {
      console.error('Unhandled rejection:', error);
    }
  });

  const originalPush = window.history.pushState;
  const originalReplace = window.history.replaceState;

  const clearToastsOnNavigation = () => {
    clearAllToasts();
  };

  window.history.pushState = function (...args) {
    originalPush.apply(this, args);
    clearToastsOnNavigation();
  };

  window.history.replaceState = function (...args) {
    originalReplace.apply(this, args);
    clearToastsOnNavigation();
  };

  window.addEventListener('popstate', clearToastsOnNavigation);

  window.addEventListener('error', (event) => {
    if (event.target?.tagName === 'SCRIPT' || event.target?.tagName === 'LINK') {
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      console.error('Global error:', event.error);
    }
  });
};

setupGlobalErrorHandlers();

function App() {
  const { isAuthenticated, user } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated && user) {
      realtimeService.connect();
      realtimeService.requestNotificationPermission();
    } else {
      realtimeService.disconnect();
    }

    return () => {
      realtimeService.disconnect();
      clearAllToasts();
    };
  }, [isAuthenticated, user]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster
        position="top-right"
        containerStyle={{
          top: 20,
          right: 20,
        }}
        toastOptions={{
          success: {
            duration: 3000,
            style: {
              background: '#D1FAE5',
              color: '#065F46',
              borderRadius: '12px',
              fontSize: '14px',
              padding: '12px 16px',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
              maxWidth: '380px',
            },
            iconTheme: {
              primary: '#10B981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            style: {
              background: '#FEE2E2',
              color: '#991B1B',
              borderRadius: '12px',
              fontSize: '14px',
              padding: '12px 16px',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
              maxWidth: '380px',
            },
            iconTheme: {
              primary: '#DC2626',
              secondary: '#fff',
            },
          },
          loading: {
            style: {
              background: '#F3F4F6',
              color: '#374151',
              borderRadius: '12px',
              fontSize: '14px',
              padding: '12px 16px',
              maxWidth: '380px',
            },
          },
        }}
        limit={3}
      />

      <Routes>
        {/* Public Routes */}
        <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" /> : <HomePage />} />

        {/* Legal Pages - Public */}
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />

        {/* Auth Routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-phone" element={<VerifyPhonePage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Route>

        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<MainLayout />}>
            {/* Core App Routes */}
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/wallet" element={<WalletPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/airtime" element={<AirtimePage />} />
            <Route path="/data" element={<DataPage />} />
            <Route path="/bills" element={<BillsPage />} />
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/community/profile/:userId" element={<UserProfilePage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/wallet/funding/callback" element={<FundingCallbackPage />} />
            <Route path="/community/reports" element={<UserReportsPage />} />
            <Route path="/community/post/:postId" element={<PostPage />} />

            {/* Marketplace Routes */}
            <Route path="/marketplace/businesses" element={<MarketplaceBusinessesPage />} />
            <Route path="/community/marketplace/business/:businessId" element={<BusinessProfilePage />} />
            <Route path="/marketplace" element={<MarketplacePage />} />

            {/* Product Management */}
            <Route path="/marketplace/business/:businessId/products/manage" element={<ProductManagementPage />} />
            <Route path="/marketplace/business/:businessId/products" element={<ProductListPage />} />

            {/* Service Management */}
            <Route path="/marketplace/business/:businessId/services/manage" element={<ServiceManagementPage />} />
            <Route path="/marketplace/business/:businessId/services" element={<ServiceListPage />} />

            {/* Order Management */}
            <Route path="/marketplace/business/:businessId/orders" element={<OrderManagementPage />} />
            <Route path="/marketplace/orders" element={<MyOrdersPage />} />
            <Route path="/order/success" element={<OrderSuccessPage />} />

            <Route path="/marketplace/favorites" element={<FavoritesPage />} />
          </Route>
        </Route>

        {/* Admin Routes */}
        <Route element={<ProtectedRoute requiredRole="ADMIN" />}>
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/admin/dashboard" element={<AdminDashboardPage />} />

            <Route path="/admin/announcements" element={<AdminAnnouncementsPage />} />
            <Route path="/admin/promotions" element={<AdminPromotionsPage />} />

            <Route path="/admin/marketplace/businesses" element={<AdminBusinessesPage />} />
            <Route path="/admin/marketplace/reviews" element={<AdminReviewsPage />} />
            <Route path="/admin/marketplace/orders" element={<AdminOrdersPage />} />
          </Route>
        </Route>

        {/* 404 Route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Global Overlay - Renders above everything */}
      <GlobalOverlay />
    </div>
  );
}

export default App;